import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  tokens,
  makeStyles,
  shorthands,
  Card,
  Text,
  Button,
  Checkbox,
  Tag,
  Tooltip,
  ProgressBar,
  Slider,
} from '@fluentui/react-components';
import {
  ArrowSyncRegular,
  CopyRegular,
  EyeRegular,
  EyeOffRegular,
  CheckmarkRegular,
  LockClosedRegular,
} from '@fluentui/react-icons';
import type { PinGeneratorConfig } from '../../types';
import { DEFAULT_PIN_CONFIG } from '../../constants';
import { generatePin, evaluatePinStrength } from '../../utils/pinGenerator';

const useStyles = makeStyles({
  card: {
    width: '100%',
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('12px'),
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    border: 'none',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  stepBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorBrandBackground,
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  cardSubtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  pinDisplayArea: {
    ...shorthands.padding('20px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('12px'),
  },
  pinRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  pinText: {
    flexGrow: 1,
    fontFamily: "'Cascadia Code', 'Fira Code', Consolas, monospace",
    fontSize: '22px',
    fontWeight: 600,
    letterSpacing: '3px',
    color: tokens.colorNeutralForeground1,
    userSelect: 'all',
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
  },
  pinMasked: {
    flexGrow: 1,
    fontFamily: "'Cascadia Code', 'Fira Code', Consolas, monospace",
    fontSize: '22px',
    letterSpacing: '4px',
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
  },
  pinActions: {
    display: 'flex',
    gap: '2px',
    flexShrink: 0,
  },
  strengthSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '12px',
  },
  strengthBar: {
    flexGrow: 1,
  },
  charCountLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    marginTop: '16px',
    marginBottom: '4px',
    paddingLeft: '14px',
  },
  sliderSection: {
    display: 'flex',
    alignItems: 'center',
  },
  slider: {
    flexGrow: 1,
    width: '100%',
  },
  optionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: '8px',
    ...shorthands.padding('0', '20px'),
  },
  regenRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
  },
});

interface PinGeneratorProps {
  pin: string;
  onPinGenerated: (pin: string) => void;
}

export function PinGenerator({ pin, onPinGenerated }: PinGeneratorProps) {
  const styles = useStyles();
  const [config, setConfig] = useState<PinGeneratorConfig>(DEFAULT_PIN_CONFIG);
  const [showPin, setShowPin] = useState(false);
  const [copied, setCopied] = useState(false);

  const strength = useMemo(() => evaluatePinStrength(config), [config]);

  const strengthValue = useMemo((): number => {
    switch (strength) {
      case 'Very Strong': return 1;
      case 'Strong': return 0.75;
      case 'Fair': return 0.5;
      case 'Weak': return 0.25;
      default: return 0.1;
    }
  }, [strength]);

  const strengthColor = useMemo((): 'success' | 'warning' | 'error' | 'brand' => {
    switch (strength) {
      case 'Very Strong': return 'success';
      case 'Strong': return 'success';
      case 'Fair': return 'warning';
      case 'Weak': return 'error';
      default: return 'error';
    }
  }, [strength]);

  const tagAppearance = useMemo(() => {
    switch (strength) {
      case 'Very Strong':
      case 'Strong':
        return { bg: tokens.colorPaletteGreenBackground2, fg: tokens.colorPaletteGreenForeground2 };
      case 'Fair':
        return { bg: tokens.colorPaletteYellowBackground2, fg: tokens.colorPaletteYellowForeground2 };
      default:
        return { bg: tokens.colorPaletteRedBackground2, fg: tokens.colorPaletteRedForeground2 };
    }
  }, [strength]);

  // Auto-generate PIN when config changes (real-time reactivity)
  useEffect(() => {
    const newPin = generatePin(config);
    onPinGenerated(newPin);
    setShowPin(true);
    setCopied(false);
  }, [config, onPinGenerated]);

  const handleRegenerate = useCallback(() => {
    const newPin = generatePin(config);
    onPinGenerated(newPin);
    setShowPin(true);
    setCopied(false);
  }, [config, onPinGenerated]);

  const handleCopy = useCallback(async () => {
    if (!pin) return;
    await navigator.clipboard.writeText(pin);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [pin]);

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.stepBadge}>2</div>
        <div>
          <Text className={styles.cardTitle} block>Generate PIN</Text>
          <Text className={styles.cardSubtitle} block>
            Configure and generate a secure PIN for the FIDO2 security key
          </Text>
        </div>
      </div>

      {/* PIN Display */}
      {pin && (
        <div className={styles.pinDisplayArea}>
          <div className={styles.pinRow}>
            <LockClosedRegular fontSize={18} style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
            {showPin ? (
              <Text className={styles.pinText}>{pin}</Text>
            ) : (
              <Text className={styles.pinMasked}>{'•'.repeat(pin.length)}</Text>
            )}
            <div className={styles.pinActions}>
              <Tooltip content={showPin ? 'Hide' : 'Show'} relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={showPin ? <EyeOffRegular /> : <EyeRegular />}
                  onClick={() => setShowPin(!showPin)}
                />
              </Tooltip>
              <Tooltip content={copied ? 'Copied!' : 'Copy'} relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={handleCopy}
                />
              </Tooltip>
              <Tooltip content="Regenerate" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<ArrowSyncRegular />}
                  onClick={handleRegenerate}
                />
              </Tooltip>
            </div>
          </div>
          <div className={styles.strengthSection}>
            <ProgressBar
              className={styles.strengthBar}
              value={strengthValue}
              color={strengthColor}
              thickness="large"
              shape="rounded"
            />
            <Tag
              size="extra-small"
              shape="circular"
              appearance="filled"
              style={{ backgroundColor: tagAppearance.bg, color: tagAppearance.fg }}
            >
              {strength}
            </Tag>
          </div>
        </div>
      )}

      {/* Length slider — outside the PIN box */}
      <Text className={styles.charCountLabel}>{config.length} Characters</Text>
      <div className={styles.sliderSection}>
        <Slider
          className={styles.slider}
          min={4}
          max={64}
          value={config.length}
          onChange={(_, data) =>
            setConfig((c) => ({ ...c, length: data.value }))
          }
        />
      </div>

      {/* Character options — horizontal checkboxes */}
      <div className={styles.optionsRow}>
        <Checkbox
          label="Uppercase"
          checked={config.includeUppercase}
          onChange={(_, data) => setConfig((c) => ({ ...c, includeUppercase: !!data.checked }))}
        />
        <Checkbox
          label="Lowercase"
          checked={config.includeLowercase}
          onChange={(_, data) => setConfig((c) => ({ ...c, includeLowercase: !!data.checked }))}
        />
        <Checkbox
          label="Numbers"
          checked={config.includeNumbers}
          onChange={(_, data) => setConfig((c) => ({ ...c, includeNumbers: !!data.checked }))}
        />
        <Checkbox
          label="Symbols"
          checked={config.includeSymbols}
          onChange={(_, data) => setConfig((c) => ({ ...c, includeSymbols: !!data.checked }))}
        />
      </div>
    </Card>
  );
}
